// src/App.js
import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { LoadingState } from './components/feedback/LoadingState';
import ProtocolHandler from './components/RemoteCard/protocol-handler';
import { useRecoilState } from 'recoil';
import { PopupAtom } from './atoms/popupAtom';
import { AuthProvider, useAuth } from './context/AuthContext';

// 스크린 컴포넌트들
import Dashboard from './screen/Home/index';
import Database from './screen/Database';
import MyStorage from './screen/MyStorage';
import Kubernetes from './screen/Kubernetes';
import Serverless from './screen/Serverless';
import Network from './screen/Network';
import ContainerRegistry from './screen/ContainerRegistry';
import PodmanOverview from './screen/PodmanOverview';
import LabPilot from './screen/LabPilot';
import ManagementPlan from './screen/ManagementPlan';
import Deployments from './screen/Deployments';
import Software from './screen/Software';
import ReleasePlanner from './screen/ReleasePlanner';
import Operations from './screen/Operations';
import InfrastructureOverview from './screen/Operations/InfrastructureOverview';
import Monitoring from './screen/Monitoring';
import AIServiceDesign from './screen/AIServiceDesign';
import DeployMonitor from './screen/DeployMonitor';
import Guide from './screen/Guide';
import RtRemote from './screen/RtRemote';
import Home from './screen/Home';
import './App.css';

import Standardinfo from './screen/Standardinfo/index';
import {Compute} from './screen/Compute';
import Infra from './screen/Compute';
import Event from './screen/Event';
import ProjectPage from './screen/Project/indexN';
import Popup from './components/Popup';
import Project from './screen/Project/indexN';
import ProjectForm from './screen/Project/ProjectSubmit';
import { CloudCompute } from "./screen/CloudCompute";
import AccountManagement from './screen/Account';
import LoginPage from './screen/LoginPage';
import SignupPage from './screen/SignupPage';
import PasswordRecoveryPage from './screen/PasswordRecoveryPage';
import MyPage from './screen/MyPage';
import ConsolePage from './screen/Console';
import SupportPage from './screen/Support';
import ReferralPage from './screen/Referral';
import BillingPage from './screen/Billing';
import './components/consoleWorkspaces.css';

// 인증이 필요한 라우트를 위한 보호 컴포넌트
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();
  
  if (loading) {
    return <LoadingState fullPage label="로그인 상태를 확인하고 있습니다" />;
  }
  
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// App 컴포넌트는 AuthProvider 안에 들어가야 하므로, 내부 컴포넌트 분리
const AppRoutes = () => {
  const [ModalInfo] = useRecoilState(PopupAtom);
  const { isLoggedIn, loading } = useAuth();
  
  if (loading) {
    return <LoadingState fullPage label="클라우드에 연결하고 있습니다" />;
  }

  return (
    <Routes>
      {/* 콘솔은 팝업 창 전용 (사이드바/헤더 없음) */}
      <Route path="/console/:infraId" element={<ProtectedRoute><ConsolePage /></ProtectedRoute>} key="Console" />

      {/* 그 외 모든 라우트는 Layout (사이드바 포함) 안에서 렌더 */}
      <Route path="/*" element={
        <Layout>
          <div id="Contents" className="MasterPanel">
            <Routes>
              <Route path="/" element={isLoggedIn ? <Dashboard /> : <Navigate to="/login" replace />} />
              <Route path="/compute" element={
                <ProtectedRoute>
                  <Infra/>
                </ProtectedRoute>
              } />
              <Route path="/Storage" element={<ProtectedRoute><MyStorage/></ProtectedRoute>} key="Storage" />
              <Route path="/Kubernetes" element={<ProtectedRoute><Kubernetes/></ProtectedRoute>} key="Kubernetes" />
              <Route path="/Serverless" element={<ProtectedRoute><Serverless/></ProtectedRoute>} key="Serverless" />
              <Route path="/Registry" element={<ProtectedRoute><ContainerRegistry/></ProtectedRoute>} key="ContainerRegistry" />
              <Route path="/Containers" element={<ProtectedRoute><PodmanOverview/></ProtectedRoute>} key="PodmanOverview" />
              <Route path="/labs" element={<ProtectedRoute><LabPilot/></ProtectedRoute>} key="LabPilot" />
              <Route path="/management-plan" element={<ProtectedRoute><ManagementPlan/></ProtectedRoute>} key="ManagementPlan" />
              <Route path="/deploy" element={<ProtectedRoute><Deployments/></ProtectedRoute>} key="Deployments" />
              <Route path="/software" element={<ProtectedRoute><Software/></ProtectedRoute>} key="Software" />
              <Route path="/deploy/plan" element={<ProtectedRoute><ReleasePlanner/></ProtectedRoute>} key="ReleasePlanner" />
              <Route path="/infrastructure" element={<ProtectedRoute><Dashboard/></ProtectedRoute>} />
              <Route path="/monitoring" element={<ProtectedRoute><Monitoring/></ProtectedRoute>} />
              <Route path="/infrastructure/sources" element={<ProtectedRoute><InfrastructureOverview/></ProtectedRoute>} />
              <Route path="/operations" element={<ProtectedRoute><Operations/></ProtectedRoute>} key="Operations" />
              <Route path="/ai-services" element={<ProtectedRoute><AIServiceDesign/></ProtectedRoute>} key="AIServiceDesign" />
              <Route path="/monitor" element={<ProtectedRoute><DeployMonitor/></ProtectedRoute>} key="DeployMonitor" />
              <Route path="/Database" element={<ProtectedRoute><Database/></ProtectedRoute>} key="Database" />
              <Route path="/Network" element={<ProtectedRoute><Network/></ProtectedRoute>} key="Network" />
              <Route path="/rtremote" element={<ProtectedRoute><RtRemote/></ProtectedRoute>} key="RtRemote" />
              <Route path="/account" element={<ProtectedRoute><AccountManagement/></ProtectedRoute>} key="Account" />
              <Route path="/Account" element={<Navigate to="/account" replace />} />
              <Route path="/mypage" element={<Navigate to="/account" replace />} />
              <Route path="/support" element={<ProtectedRoute><SupportPage/></ProtectedRoute>} key="Support" />
              <Route path="/referral" element={<ProtectedRoute><ReferralPage/></ProtectedRoute>} key="Referral" />
              <Route path="/billing" element={<ProtectedRoute><BillingPage/></ProtectedRoute>} key="Billing" />
              <Route path="/guide" element={<ProtectedRoute><Guide/></ProtectedRoute>} key="Guide" />

              {/* 공개 라우트 */}
              <Route path="/login" element={isLoggedIn ? <Navigate to="/" replace /> : <LoginPage />} key="login" />
              <Route path="/signup" element={isLoggedIn ? <Navigate to="/" replace /> : <SignupPage />} key="signup" />
              <Route path="/forgot-password" element={isLoggedIn ? <Navigate to="/" replace /> : <PasswordRecoveryPage />} key="forgot-password" />

              {/* 다른 보호된 라우트들 */}
              <Route path="/Event" element={<ProtectedRoute><Event/></ProtectedRoute>} key="Event" />
              <Route path="/ProjectPage" element={<ProtectedRoute><ProjectPage/></ProtectedRoute>} key="ProjectPage" />
              <Route path="/ProjectForm" element={<ProtectedRoute><ProjectForm/></ProtectedRoute>} key="ProjectForm" />
              <Route path="/protocol-handler" element={<ProtocolHandler />} />
            </Routes>
          </div>
        </Layout>
      } />
    </Routes>
  );
};

// BrowserRouter는 AuthProvider 외부에 있어야 합니다
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <header className="App-header"></header>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
